import { Check, GripVertical, PackageCheck, Upload, X } from 'lucide-react';
import { UNASSIGNED_STORE } from '../lib/constants.js';
import { IconButton } from './ui.jsx';

export function ReceiptScanner({ stores, selectedStore, receiptText, receiptItems, groceries, status, trips, onStoreChange, onTextChange, onParse, onImage, onMatchChange, onApply }) {
  return (
    <section className="panel wide receipt-panel">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Shopping trip</p>
          <h3>Receipt scanner</h3>
        </div>
        <label className="upload-button">
          <Upload size={16} />
          Scan image
          <input accept="image/*" onChange={(event) => onImage(event.target.files?.[0])} type="file" />
        </label>
      </div>
      <div className="receipt-controls">
        <label>
          Store
          <select value={selectedStore} onChange={(event) => onStoreChange(event.target.value)}>
            {stores.map((store) => <option key={store} value={store}>{store}</option>)}
          </select>
        </label>
        <label>
          Receipt text
          <textarea
            value={receiptText}
            onChange={(event) => onTextChange(event.target.value)}
            placeholder="Paste receipt lines here, e.g. Greek Yogurt 5.99"
            rows={5}
          />
        </label>
        <button className="primary-button" onClick={onParse} type="button">Parse receipt</button>
      </div>
      {status && <p className="receipt-status">{status}</p>}
      {receiptItems.length > 0 && (
        <div className="receipt-match-list">
          {receiptItems.map((item) => (
            <div className="receipt-match-row" key={item.id}>
              <div>
                <strong>{item.name}</strong>
                <span>${item.price}</span>
              </div>
              <select
                value={item.matchId}
                onChange={(event) => onMatchChange(item.id, event.target.value)}
                aria-label={`Match ${item.name}`}
              >
                <option value="">No match</option>
                {groceries.map((grocery) => (
                  <option key={grocery.id} value={grocery.id}>{grocery.name}</option>
                ))}
              </select>
            </div>
          ))}
          <button className="primary-button" onClick={onApply} type="button">Apply matched prices</button>
        </div>
      )}
      {trips.length > 0 && (
        <div className="receipt-trip-preview">
          <strong>Recent trips</strong>
          {trips.slice(0, 3).map((trip) => (
            <span key={trip.id}>{trip.date} / {trip.store} / {trip.items.length} items</span>
          ))}
        </div>
      )}
    </section>
  );
}

export function GroceryStoreColumn({ title, items, editable = false, onRename, onRemoveStore, onDropItem, onToggleBought, onPriceChange, onRemove, onCompleteTrip }) {
  const sortedItems = [...items].sort((a, b) => a.name.localeCompare(b.name));
  const boughtCount = items.filter((item) => item.bought).length;
  return (
    <section
      className="store-column"
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault();
        const id = event.dataTransfer.getData('text/plain');
        if (id) onDropItem(id);
      }}
    >
      <div className="store-column-heading">
        {editable ? (
          <input
            className="store-name-input"
            defaultValue={title}
            onBlur={(event) => onRename(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && event.currentTarget.blur()}
            aria-label={`${title} store name`}
          />
        ) : (
          <h4>{title}</h4>
        )}
        {editable && <IconButton label="Remove store" icon={X} onClick={onRemoveStore} />}
      </div>
      <div className="store-column-scroll">
        {sortedItems.map((item) => (
          <div
            className="inventory-row grocery-row"
            key={item.id}
            draggable
            onDragStart={(event) => event.dataTransfer.setData('text/plain', item.id)}
          >
            <GripVertical className="drag-handle" size={15} aria-hidden="true" />
            <button
              className={item.bought ? 'check-circle checked' : 'check-circle'}
              onClick={() => onToggleBought(item.id)}
              type="button"
              aria-label={`Toggle ${item.name}`}
            >
              <Check size={14} />
            </button>
            <span>{item.name}</span>
            <input
              className="price-input"
              value={item.price}
              onChange={(event) => onPriceChange(item.id, event.target.value)}
              inputMode="decimal"
              placeholder="$"
              aria-label={`${item.name} price`}
            />
            <IconButton label="Remove" icon={X} onClick={() => onRemove(item.id)} />
          </div>
        ))}
        {sortedItems.length === 0 && <p className="empty-drop-text">Drop groceries here</p>}
      </div>
      {onCompleteTrip && boughtCount > 0 && (
        <button className="complete-trip-button" onClick={onCompleteTrip} type="button">
          <PackageCheck size={15} /> Complete trip · {boughtCount} bought
        </button>
      )}
    </section>
  );
}

export function normalizeMatchText(value) {
  return value.toLowerCase().replace(/[^a-z0-9 ]/g, ' ').replace(/\s+/g, ' ').trim();
}

export function parseReceiptItems(text) {
  return text
    .split('\n')
    .map((line) => line.trim())
    .flatMap((line) => {
      const match = line.match(/(.+?)\s+\$?([0-9]+\.[0-9]{2})\s*$/);
      if (!match) return [];
      const name = match[1]
        .replace(/\b(qty|subtotal|total|tax|visa|mastercard|change|cash)\b/gi, '')
        .replace(/\s+/g, ' ')
        .trim();
      const price = match[2];
      if (!name || /^(sub ?total|total|tax)$/i.test(name)) return [];
      return { id: crypto.randomUUID(), raw: line, name, price, matchId: '', confidence: 0 };
    });
}

export function getBestReceiptMatch(receiptName, groceries) {
  const receiptWords = new Set(normalizeMatchText(receiptName).split(' ').filter((w) => w.length > 2));
  return groceries.reduce((best, grocery) => {
    const groceryWords = new Set(normalizeMatchText(grocery.name).split(' ').filter((w) => w.length > 2));
    if (!receiptWords.size || !groceryWords.size) return best;
    const overlap = [...receiptWords].filter((w) => groceryWords.has(w)).length;
    const confidence = overlap / Math.max(receiptWords.size, groceryWords.size);
    return confidence > best.confidence ? { id: grocery.id, confidence } : best;
  }, { id: '', confidence: 0 });
}
